import type { IGetTableInfoDto, ITableColumnClassDto } from '@dbl/services/dataSource';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type{ IDimensionOption, ITooltipExtraItem } from '../types';

// 判断字段是否属于“维度”可选范围（兼容不同数据结构标记方式）
export const isDimensionColumn = (
  columnClass: ITableColumnClassDto,
  column: IColumnInfo,
): boolean => {
  if (column?.is_dimension) {
    return true;
  }
  if (column?.attribute === 0 || column?.attribute === 1) {
    return true;
  }
  const classNameEn = (columnClass?.name_en || '').toLowerCase();
  return columnClass?.name === '维度' || classNameEn.includes('dimension');
};

// 获取分组内显示的字段文案（仅字段名）
export const getDimensionFieldText = (column: IColumnInfo): string => {
  return column?.column_display || column?.column_name || '';
};

// 获取保存/回显使用的字段标签
export const getDimensionOptionLabel = (
  column: IColumnInfo,
): string => {
  const label = getDimensionFieldText(column);
  return `${label}`;
};

// 生成下拉分组标题文案
export const getTableGroupLabel = (table: IGetTableInfoDto, tableCode: string): string => {
  const tableName = table?.customize_name || table?.customize_name_en || table?.table_name || tableCode;
  return `${tableName}`;
};

// 计算字段的唯一 key（用于去重和选中态判断）
export const getFieldKey = (item: ITooltipExtraItem): string => {
  if (item?.key) {
    return item.key;
  }
  if (item?.tableCode && item?.column) {
    return `${item.tableCode}_${item.column}`;
  }
  return (item?.value || '').replace('.', '_');
};

// 构建 Select 分组映射：{ tableCode: tableLabel }
export const buildTableGroups = (
  dataTables: IGetTableInfoDto[],
): Record<string, string> => {
  const entries = dataTables.map((table, tableIndex) => {
    const tableCode = table?.table_code || `t${tableIndex}`;
    return [tableCode, getTableGroupLabel(table, tableCode)] as [string, string];
  });
  const groups: Record<string, string> = Object.fromEntries(entries);
  return groups;
};

// 从数据表结构中提取可插入 tooltip 的“维度字段”选项
export const buildDimensionOptions = (
  dataTables: IGetTableInfoDto[],
): IDimensionOption[] => {
  const optionsMap = new Map<string, IDimensionOption>();
  if (!dataTables?.length) {
    return [];
  }
  dataTables?.forEach((table: IGetTableInfoDto, tableIndex: number) => {
    const tableCode = table?.table_code || `t${tableIndex}`;
    (table?.column_class_list || []).forEach((columnClass) => {
      (columnClass?.columns || []).forEach((column) => {
        if (!column?.column_name || !isDimensionColumn(columnClass, column)) {
          return;
        }
        const key = `${tableCode}_${column.column_name}`;
        if (optionsMap.has(key)) {
          return;
        }
        optionsMap.set(key, {
          key,
          value: `${tableCode}.${column.column_name}`,
          text: getDimensionFieldText(column),
          label: getDimensionOptionLabel(column),
          tableCode,
          column: column.column_name,
          groupKey: tableCode,
        });
      });
    });
  });
  return Array.from(optionsMap.values());
};
